import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BusinessSettingApiItem,
  BusinessSettingsService,
  UpdateBusinessSettingDto,
  UpdateBusinessSettingsDto,
} from 'app/services/bussiness/business-settings.service';


type SettingInputType = 'switch' | 'number' | 'color' | 'textarea' | 'json' | 'text';

type SettingCategoryKey =
  | 'general'
  | 'currency'
  | 'features'
  | 'payments'
  | 'shipping'
  | 'seller'
  | 'authentication'
  | 'social'
  | 'appearance'
  | 'homepage'
  | 'contact'
  | 'seo'
  | 'system'
  | 'other';

interface BusinessSetting {
  type: string;
  value: unknown;
  draftValue: unknown;
  label: string;
  category: SettingCategoryKey;
  inputType: SettingInputType;
  dirty: boolean;
  saving: boolean;
  saveError: string;
}

interface SettingCategory {
  key: SettingCategoryKey;
  label: string;
  description: string;
  icon: string;
  settings: BusinessSetting[];
}

@Component({
  selector: 'app-business-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './business-settings.component.html',
  styleUrls: ['./business-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessSettingsComponent implements OnInit {
  private readonly businessSettingsService = inject(BusinessSettingsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly categoryMeta: ReadonlyArray<Omit<SettingCategory, 'settings'>> = [
    // { key: 'general', label: 'General', description: 'Website and core store information', icon: '⚙' },
    // { key: 'currency', label: 'Currency', description: 'Currency display and decimal formatting', icon: '¤' },
    // { key: 'features', label: 'Features', description: 'Store modules and feature switches', icon: '✦' },
    // { key: 'payments', label: 'Payments', description: 'Payment methods and sandbox options', icon: '▣' },
    // { key: 'shipping', label: 'Shipping', description: 'Shipping methods, costs and pickup settings', icon: '▰' },
    // { key: 'seller', label: 'Seller & Vendor', description: 'Marketplace, vendor and commission settings', icon: '♙' },
    // { key: 'authentication', label: 'Authentication', description: 'Login, verification and security settings', icon: '◉' },
    { key: 'social', label: 'Social & Tracking', description: 'Social links, chat and analytics integrations', icon: '↗' },
    // { key: 'appearance', label: 'Appearance', description: 'Colours, logos and visual configuration', icon: '◈' },
    // { key: 'homepage', label: 'Homepage', description: 'Sliders, banners and homepage sections', icon: '⌂' },
    // { key: 'contact', label: 'Contact & Footer', description: 'Contact details, footer and copyright content', icon: '✉' },
    // { key: 'seo', label: 'SEO', description: 'Search metadata and sharing image settings', icon: '⌕' },
    // { key: 'system', label: 'System', description: 'System-level configuration and maintenance', icon: '⌘' },
    // { key: 'other', label: 'Other', description: 'Uncategorised business settings', icon: '•••' },
  ];

  settings: BusinessSetting[] = [];
  categories: SettingCategory[] = [];
  selectedCategory: SettingCategoryKey | 'all' = 'social';
  // selectedCategory: SettingCategoryKey | 'all' = 'all';
  searchTerm = '';
  isLoading = false;
  isSavingAll = false;
  errorMessage = '';
  successMessage = '';

  ngOnInit(): void {
    this.loadSettings();
  }

  get visibleCategories(): SettingCategory[] {
    const term = this.searchTerm.trim().toLowerCase();

    return this.categories
      .filter(category => this.selectedCategory === 'all' || category.key === this.selectedCategory)
      .map(category => ({
        ...category,
        settings: category.settings.filter(setting => {
          if (!term) {
            return true;
          }

          return (
            setting.type.toLowerCase().includes(term) ||
            setting.label.toLowerCase().includes(term) ||
            this.valueToSearchText(setting.draftValue).includes(term)
          );
        }),
      }))
      .filter(category => category.settings.length > 0);
  }

  get dirtyCount(): number {
    return this.settings.filter(setting => setting.dirty).length;
  }

  get totalCount(): number {
    return this.settings.length;
  }

  get populatedCount(): number {
    return this.settings.filter(setting => this.hasValue(setting.value)).length;
  }

  get emptyCount(): number {
    return this.totalCount - this.populatedCount;
  }

  trackCategory(_: number, category: SettingCategory): string {
    return category.key;
  }

  trackSetting(_: number, setting: BusinessSetting): string {
    return setting.type;
  }

  loadSettings(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.businessSettingsService
      .getBusinessSettings()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: response => {
          if (!response?.success || !Array.isArray(response.data)) {
            this.errorMessage = response?.message || 'The API returned an invalid settings response.';
            return;
          }
          this.settings = this.normaliseSettings(response.data);
          this.buildCategories();
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = this.getHttpErrorMessage(error, 'Unable to load business settings.');
        },
      });
  }

  selectCategory(category: SettingCategoryKey | 'all'): void {
    this.selectedCategory = category;
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  onValueChanged(setting: BusinessSetting, value: unknown): void {
    setting.draftValue = value;
    setting.dirty = !this.valuesAreEqual(setting.value, setting.draftValue);
    setting.saveError = '';
    this.successMessage = '';
  }

  onTextInput(setting: BusinessSetting, value: string): void {
    if (setting.inputType === 'number') {
      const parsed = value === '' ? null : Number(value);
      this.onValueChanged(setting, Number.isNaN(parsed) ? value : parsed);
      return;
    }

    this.onValueChanged(setting, value);
  }

  resetSetting(setting: BusinessSetting): void {
    setting.draftValue = this.cloneValue(setting.value);
    setting.dirty = false;
    setting.saveError = '';
  }

  saveSetting(setting: BusinessSetting): void {
    if (!setting.dirty || setting.saving) {
      return;
    }

    const validationError = this.validateSetting(setting);
    if (validationError) {
      setting.saveError = validationError;
      return;
    }

    setting.saving = true;
    setting.saveError = '';
    this.errorMessage = '';
    this.successMessage = '';

    const payload: UpdateBusinessSettingDto = {
      type: setting.type,
      value: this.prepareValueForSave(setting),
    };

    this.businessSettingsService
      .updateBusinessSetting(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          setting.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: response => {
          if (response?.success === false) {
            setting.saveError = response.message || `Could not save ${setting.label}.`;
            return;
          }

          setting.value = this.cloneValue(payload.value);
          setting.draftValue = this.cloneValue(payload.value);
          setting.dirty = false;
          this.successMessage = `${setting.label} was saved successfully.`;
        },
        error: (error: HttpErrorResponse) => {
          setting.saveError = this.getHttpErrorMessage(error, `Could not save ${setting.label}.`);
        },
      });
  }

  saveAll(): void {
    const dirtySettings = this.settings.filter(setting => setting.dirty && !setting.saving);
    if (!dirtySettings.length || this.isSavingAll) {
      return;
    }

    const invalidSetting = dirtySettings.find(setting => this.validateSetting(setting));
    if (invalidSetting) {
      invalidSetting.saveError = this.validateSetting(invalidSetting);
      this.errorMessage = `Please fix ${invalidSetting.label} before saving.`;
      return;
    }

    this.isSavingAll = true;
    this.errorMessage = '';
    this.successMessage = '';

    const payload: UpdateBusinessSettingsDto = {
      settings: dirtySettings.map(setting => ({
        type: setting.type,
        value: this.prepareValueForSave(setting),
      })),
    };

    this.businessSettingsService
      .updateBusinessSettings(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSavingAll = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: response => {
          if (response?.success === false) {
            this.errorMessage = response.message || 'Could not save the changed settings.';
            return;
          }

          for (const setting of dirtySettings) {
            setting.value = this.cloneValue(this.prepareValueForSave(setting));
            setting.draftValue = this.cloneValue(setting.value);
            setting.dirty = false;
            setting.saveError = '';
          }

          this.successMessage = `${dirtySettings.length} setting${dirtySettings.length === 1 ? '' : 's'} saved successfully.`;
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = this.getHttpErrorMessage(error, 'Could not save the changed settings.');
        },
      });
  }

  resetAllChanges(): void {
    for (const setting of this.settings) {
      this.resetSetting(setting);
    }

    this.successMessage = '';
    this.errorMessage = '';
  }

  categoryCount(key: SettingCategoryKey | 'all'): number {
    if (key === 'all') {
      return this.settings.length;
    }

    return this.settings.filter(setting => setting.category === key).length;
  }

  isSwitchEnabled(setting: BusinessSetting): boolean {
    return this.toBoolean(setting.draftValue);
  }

  displayValue(setting: BusinessSetting): string {
    if (!this.hasValue(setting.draftValue)) {
      return '';
    }

    if (setting.inputType === 'json') {
      if (typeof setting.draftValue === 'string') {
        return setting.draftValue;
      }

      try {
        return JSON.stringify(setting.draftValue, null, 2);
      } catch {
        return String(setting.draftValue);
      }
    }

    return String(setting.draftValue);
  }

  private normaliseSettings(items: BusinessSettingApiItem[]): BusinessSetting[] {
    const uniqueSettings = new Map<string, BusinessSettingApiItem>();

    for (const item of items) {
      if (!item?.type?.trim()) {
        continue;
      }
      const key = item.type.trim();
      // Exclude unwanted settings
      if (
        key.toLowerCase() === 'google_analytics' ||
        key.toLowerCase() === 'show_social_links' ||
        key.toLowerCase() === 'facebook_pixel' 
      ) 
      {
        continue;
      }
      
      const existing = uniqueSettings.get(key);
      
      // Prefer the duplicate record that actually contains a value.
      if (!existing || (!this.hasValue(existing.value) && this.hasValue(item.value))) {
        uniqueSettings.set(key, { type: key, value: item.value ?? null });
      }
    }

    return Array.from(uniqueSettings.values())
      .map(item => {
        const inputType = this.detectInputType(item.type, item.value);
        const initialValue = this.normaliseValueForInput(item.value, inputType);
console.log(inputType);
console.log(item);
        return {
          type: item.type,
          value: this.cloneValue(initialValue),
          draftValue: this.cloneValue(initialValue),
          label: this.toLabel(item.type),
          category: this.detectCategory(item.type),
          inputType,
          dirty: false,
          saving: false,
          saveError: '',
        } satisfies BusinessSetting;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private buildCategories(): void {
    this.categories = this.categoryMeta
      .map(meta => ({
        ...meta,
        settings: this.settings.filter(setting => setting.category === meta.key),
      }))
      .filter(category => category.settings.length > 0);
  }

  private detectCategory(type: string): SettingCategoryKey {
    const key = type.toLowerCase();

    if (/(currency|decimal|symbol_format|no_of_decimals)/.test(key)) return 'currency';
    if (/(payment|paypal|payumoney|sslcommerz|instamojo|voguepay|payhere|iyzico|bkash|proxypay|authorizenet|aamarpay)/.test(key)) return 'payments';
    if (/(shipping|pickup|delivery)/.test(key)) return 'shipping';
    if (/(seller|vendor|commission|auction|wholesale|product_approve|product_manage)/.test(key)) return 'seller';
    if (/(login|verification|recaptcha|firebase|password|authentication)/.test(key)) return 'authentication';
    if (/(facebook|twitter|instagram|youtube|linkedin|whatsapp|google|contact_address|contact_phone|contact_email|appstore|order_tracking|region_diameter|chat|analytics|pixel|social)/.test(key)) return 'social';
    if (/(logo|icon|color|theme|background|layout|text_color|format|image_format)/.test(key)) return 'appearance';
    if (/(home_|homepage|slider|banner|featured|best_selling|new_product|todays_deal|flash_deal|top10|top_brands|last_viewed|category)/.test(key)) return 'homepage';
    if (/(contact|footer|copyright|helpline|widget|about_us|address|phone|email)/.test(key)) return 'contact';
    if (/^(meta_|site_name$)/.test(key)) return 'seo';
    if (/(maintenance|timezone|current_version|uploaded|custom_alert|notification|minimum_order|item_name)/.test(key)) return 'system';
    if (/(activation|active|system|checkout|conversation|wallet|coupon|classified|product_query|floating)/.test(key)) return 'features';
    if (/(website|motto|header_menu|header_nav|topbar)/.test(key)) return 'general';

    return 'other';
  }

  private detectInputType(type: string, value: unknown): SettingInputType {
    const key = type.toLowerCase();

    if ( 
        /(activation|active$|_sandbox$|^show_|^use_|^cash_payment$|^wallet_system$|^coupon_system$|maintenance_mode|full_width|outline$|product_manage|product_approve|email_verification|guest_checkout|best_selling$|header_stikcy|order_tracking)/.test(key) 
      ) { 
        return 'switch'; 
      }

    if (/(color)/.test(key)) return 'color';
    if (/(no_of_decimals|amount|cost|commission|version|region_diameter)/.test(key)) return 'number';
    if (/(description|copyright|keywords|labels|links|menu_text|card_text|subtitle|motto)/.test(key)) return 'textarea';

    if (
      Array.isArray(value) ||
      (typeof value === 'object' && value !== null) ||
      (typeof value === 'string' && /^[\[{]/.test(value.trim()))
    ) {
      return 'json';
    }

    return 'text';
  }

  private normaliseValueForInput(value: unknown, inputType: SettingInputType): unknown {
    if (value === undefined || value === null) {
      if (inputType === 'switch') return false;
      return '';
    }

    if (inputType === 'switch') {
      return this.toBoolean(value);
    }

    return value;
  }

  private validateSetting(setting: BusinessSetting): string {
    if (setting.inputType !== 'json') {
      return '';
    }

    const value = setting.draftValue;
    if (!this.hasValue(value) || typeof value !== 'string') {
      return '';
    }

    try {
      JSON.parse(value);
      return '';
    } catch {
      return 'Enter valid JSON before saving.';
    }
  }

  private prepareValueForSave(setting: BusinessSetting): unknown {
    if (setting.inputType === 'json' && typeof setting.draftValue === 'string') {
      const trimmed = setting.draftValue.trim();
      if (!trimmed) {
        return null;
      }

      try {
        return JSON.parse(trimmed);
      } catch {
        return setting.draftValue;
      }
    }

    return setting.draftValue === '' ? null : setting.draftValue;
  }

  private valuesAreEqual(a: unknown, b: unknown): boolean {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return a === b;
    }
  }

  private cloneValue<T>(value: T): T {
    if (value === null || value === undefined || typeof value !== 'object') {
      return value;
    }

    try {
      return structuredClone(value);
    } catch {
      return JSON.parse(JSON.stringify(value)) as T;
    }
  }

  private hasValue(value: unknown): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;

    const normalised = String(value ?? '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'on', 'enabled', 'active'].includes(normalised);
  }

  private toLabel(type: string): string {
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
      .replace(/Stikcy/g, 'Sticky')
      .replace(/Cupon/g, 'Coupon');
  }

  private valueToSearchText(value: unknown): string {
    try {
      return JSON.stringify(value ?? '').toLowerCase();
    } catch {
      return String(value ?? '').toLowerCase();
    }
  }

  private getHttpErrorMessage(error: HttpErrorResponse, fallback: string): string {
    if (error.status === 0) {
      return 'Cannot connect to the API. Check the server URL, proxy and CORS configuration.';
    }

    return (
      error.error?.message ||
      error.error?.error ||
      error.message ||
      `${fallback} HTTP ${error.status}.`
    );
  }
}
